import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';

import { ApiService } from '../../core/services/api.service';
import { PmAdvancedApiService } from './pm-advanced.service';
import { PmTimelineViewComponent } from './pm-timeline-view.component';
import { IssueApiService } from '../issues/issue.service';
import { ProjectComponentDto } from '../issues/issue.models';
import {
  CUSTOM_FIELD_TYPE_OPTIONS,
  CustomFieldDefinitionDto,
  DEPLOYMENT_STATUS_OPTIONS,
  IssueSearchHit,
  MILESTONE_STATUS_OPTIONS,
  PortfolioRoadmapDto,
  PortfolioSummaryDto,
  ProjectMilestoneDto,
  ProjectVersionDto,
  ReleaseCalendarEntryDto,
  ReleaseChecklistItemDto,
  SavedIssueFilterDto,
  VERSION_STATUS_OPTIONS
} from './pm-advanced.models';
import { healthRagClass } from '../shared/health.utils';

interface ProjectRow { id: string; name: string; code?: string; }
interface CalendarMonthCell {
  key: string;
  day: number;
  inMonth: boolean;
  date: string;
  entries: ReleaseCalendarEntryDto[];
}

@Component({
  selector: 'app-pm-advanced',
  standalone: true,
  imports: [
    CommonModule, FormsModule, RouterLink,
    MatFormFieldModule, MatSelectModule, MatInputModule, MatButtonModule,
    MatIconModule, MatProgressSpinnerModule, MatCheckboxModule, MatTabsModule,
    MatTableModule, MatPaginatorModule, MatTooltipModule, MatChipsModule,
    PmTimelineViewComponent
  ],
  templateUrl: './pm-advanced.component.html',
  styleUrl: './pm-advanced.component.scss'
})
export class PmAdvancedComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly pm = inject(PmAdvancedApiService);
  private readonly issuesApi = inject(IssueApiService);
  private readonly route = inject(ActivatedRoute);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly projects = signal<ProjectRow[]>([]);
  readonly selectedProjectId = signal<string | null>(null);
  readonly versions = signal<ProjectVersionDto[]>([]);
  readonly milestones = signal<ProjectMilestoneDto[]>([]);
  readonly customFields = signal<CustomFieldDefinitionDto[]>([]);
  readonly filters = signal<SavedIssueFilterDto[]>([]);
  readonly searchHits = signal<IssueSearchHit[]>([]);
  readonly searchTotal = signal(0);
  readonly releaseCalendar = signal<ReleaseCalendarEntryDto[]>([]);
  readonly loadingCalendar = signal(false);
  readonly components = signal<ProjectComponentDto[]>([]);
  readonly portfolios = signal<PortfolioSummaryDto[]>([]);
  readonly loadingPortfolios = signal(false);
  readonly portfolioSource = signal<'api' | 'accounts'>('api');
  readonly selectedPortfolioId = signal<string | null>(null);
  readonly portfolioRoadmap = signal<PortfolioRoadmapDto | null>(null);
  readonly loadingPortfolioRoadmap = signal(false);
  readonly checklistItems = signal<ReleaseChecklistItemDto[]>([]);
  readonly selectedVersionId = signal<string | null>(null);
  readonly loadingChecklist = signal(false);

  versionName = '';
  versionDescription = '';
  versionStartDate = '';
  versionReleaseDate = '';
  versionReleaseNotes = '';
  editingVersionId = '';
  deployEnvironment = 'production';
  calendarFrom = '';
  calendarTo = '';
  calendarView: 'table' | 'month' = 'table';
  calendarMonth = new Date();
  componentName = '';
  componentDescription = '';
  milestoneName = '';
  milestoneDescription = '';
  milestoneTargetDate = '';
  milestoneVersionId = '';
  milestoneStatus = 0;
  editingMilestoneId = '';
  newChecklistTitle = '';
  fieldName = '';
  fieldKey = '';
  fieldType = 0;
  fieldOptions = '';
  jql = 'status = Open';
  filterName = '';
  filterShared = false;
  selectedFilterId = '';
  editingFilterId = '';
  searchPage = 0;
  searchPageSize = 25;
  selectedTabIndex = 0;

  readonly versionStatuses = VERSION_STATUS_OPTIONS;
  readonly milestoneStatuses = MILESTONE_STATUS_OPTIONS;
  readonly deploymentStatuses = DEPLOYMENT_STATUS_OPTIONS;
  readonly fieldTypes = CUSTOM_FIELD_TYPE_OPTIONS;
  readonly searchColumns = ['key', 'summary', 'typeLabel', 'statusLabel', 'assigneeName'];
  readonly versionColumns = ['name', 'dates', 'status', 'approval', 'checklist', 'deployment', 'progress', 'actions'];
  readonly milestoneColumns = ['name', 'targetDate', 'version', 'status', 'actions'];
  readonly calendarColumns = ['name', 'releaseDate', 'status', 'deployment', 'notes'];
  readonly checklistColumns = ['done', 'title', 'actions'];
  readonly componentColumns = ['name', 'description', 'lead', 'actions'];

  ngOnInit(): void {
    const qp = this.route.snapshot.queryParamMap;
    const tab = qp.get('tab');
    if (tab === 'calendar') this.selectedTabIndex = 4;
    if (tab === 'search') this.selectedTabIndex = 3;
    if (tab === 'timeline') this.selectedTabIndex = 5;
    if (tab === 'portfolio') this.selectedTabIndex = 6;

    this.loadPortfolios(qp.get('accountId'));

    this.api.get<{ items?: ProjectRow[] } | ProjectRow[]>('v1/projects', { take: 100 }).subscribe({
      next: (res) => {
        const items = Array.isArray(res) ? res : (res.items ?? []);
        this.projects.set(items);
        const queryProjectId = qp.get('projectId');
        const initial = queryProjectId && items.some(p => p.id === queryProjectId)
          ? queryProjectId
          : items[0]?.id;
        if (initial) {
          this.selectedProjectId.set(initial);
          this.reloadProjectData();
        }
      },
      error: (err) => this.error.set(err?.message || 'Failed to load projects.')
    });
  }

  onProjectChange(id: string): void {
    this.selectedProjectId.set(id);
    this.reloadProjectData();
  }

  reloadProjectData(): void {
    const projectId = this.selectedProjectId();
    if (!projectId) return;
    this.loading.set(true);
    this.error.set(null);

    this.pm.listVersions(projectId).subscribe({
      next: (v) => this.versions.set(Array.isArray(v) ? v : []),
      error: () => this.versions.set([])
    });
    this.pm.listMilestones(projectId).subscribe({
      next: (m) => this.milestones.set(Array.isArray(m) ? m : []),
      error: () => this.milestones.set([])
    });
    this.pm.listCustomFields(projectId).subscribe({
      next: (f) => this.customFields.set(Array.isArray(f) ? f : []),
      error: () => this.customFields.set([])
    });
    this.pm.listFilters(projectId).subscribe({
      next: (f) => {
        this.filters.set(Array.isArray(f) ? f : []);
        this.loading.set(false);
      },
      error: () => {
        this.filters.set([]);
        this.loading.set(false);
      }
    });
    this.loadReleaseCalendar();
    this.loadComponents();
  }

  loadComponents(): void {
    const projectId = this.selectedProjectId();
    if (!projectId) {
      this.components.set([]);
      return;
    }
    this.issuesApi.listComponents(projectId).subscribe({
      next: (list: ProjectComponentDto[]) => this.components.set(Array.isArray(list) ? list : []),
      error: () => this.components.set([])
    });
  }

  createComponent(): void {
    const projectId = this.selectedProjectId();
    if (!projectId || !this.componentName.trim()) return;
    this.issuesApi.createComponent(projectId, this.componentName.trim(), this.componentDescription || undefined).subscribe({
      next: () => {
        this.componentName = '';
        this.componentDescription = '';
        this.loadComponents();
      },
      error: (err: { message?: string }) => this.error.set(err?.message || 'Failed to create component.')
    });
  }

  deleteComponent(id: string): void {
    const projectId = this.selectedProjectId();
    if (!projectId) return;
    this.issuesApi.deleteComponent(projectId, id).subscribe({
      next: () => this.loadComponents(),
      error: (err: { message?: string }) => this.error.set(err?.message || 'Failed to delete component.')
    });
  }

  calendarMonthLabel(): string {
    return this.calendarMonth.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  }

  shiftCalendarMonth(delta: number): void {
    const d = new Date(this.calendarMonth);
    d.setMonth(d.getMonth() + delta);
    this.calendarMonth = d;
  }

  calendarMonthCells(): CalendarMonthCell[] {
    const year = this.calendarMonth.getFullYear();
    const month = this.calendarMonth.getMonth();
    const first = new Date(year, month, 1);
    const startPad = first.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: CalendarMonthCell[] = [];
    const byDate = new Map<string, ReleaseCalendarEntryDto[]>();
    for (const e of this.releaseCalendar()) {
      const key = (e.releaseDate || e.startDate || '').slice(0, 10);
      if (!key) continue;
      const list = byDate.get(key) ?? [];
      list.push(e);
      byDate.set(key, list);
    }
    const total = Math.ceil((startPad + daysInMonth) / 7) * 7;
    for (let i = 0; i < total; i++) {
      const dayNum = i - startPad + 1;
      const date = new Date(year, month, dayNum);
      const iso = date.toISOString().slice(0, 10);
      const inMonth = dayNum >= 1 && dayNum <= daysInMonth;
      cells.push({
        key: iso + '-' + i,
        day: date.getDate(),
        inMonth,
        date: iso,
        entries: inMonth ? (byDate.get(iso) ?? []) : []
      });
    }
    return cells;
  }

  loadReleaseCalendar(): void {
    const projectId = this.selectedProjectId();
    if (!projectId) return;
    if (!this.calendarFrom || !this.calendarTo) {
      const today = new Date();
      const from = new Date(today);
      from.setDate(from.getDate() - 30);
      const to = new Date(today);
      to.setDate(to.getDate() + 90);
      if (!this.calendarFrom) this.calendarFrom = from.toISOString().slice(0, 10);
      if (!this.calendarTo) this.calendarTo = to.toISOString().slice(0, 10);
    }
    this.loadingCalendar.set(true);
    this.pm.getReleaseCalendar(projectId, this.calendarFrom || undefined, this.calendarTo || undefined).subscribe({
      next: (cal) => {
        const entries = cal?.entries ?? (Array.isArray(cal) ? cal as unknown as ReleaseCalendarEntryDto[] : []);
        this.releaseCalendar.set(Array.isArray(entries) ? entries : []);
        this.loadingCalendar.set(false);
      },
      error: () => {
        this.releaseCalendar.set(this.versions().map(v => ({
          id: v.id,
          versionId: v.id,
          name: v.name,
          releaseDate: v.releaseDate,
          startDate: v.startDate,
          status: v.status,
          statusLabel: v.statusLabel,
          deploymentStatus: v.deploymentStatus,
          deploymentStatusLabel: v.deploymentStatusLabel,
          releaseNotes: v.releaseNotes
        })));
        this.loadingCalendar.set(false);
      }
    });
  }

  createVersion(): void {
    const projectId = this.selectedProjectId();
    if (!projectId || !this.versionName.trim()) return;
    this.pm.createVersion({
      projectId,
      name: this.versionName.trim(),
      description: this.versionDescription || undefined,
      startDate: this.versionStartDate || undefined,
      releaseDate: this.versionReleaseDate || undefined
    }).subscribe({
      next: () => {
        this.versionName = '';
        this.versionDescription = '';
        this.versionStartDate = '';
        this.versionReleaseDate = '';
        this.reloadProjectData();
      },
      error: (err) => this.error.set(err?.message || 'Failed to create version.')
    });
  }

  releaseVersion(v: ProjectVersionDto): void {
    const notes = this.editingVersionId === v.id ? this.versionReleaseNotes : undefined;
    this.pm.releaseVersion(v.id, notes ? { releaseNotes: notes } : undefined).subscribe({
      next: () => {
        this.versionReleaseNotes = '';
        this.editingVersionId = '';
        this.reloadProjectData();
      },
      error: () => {
        this.pm.updateVersion(v.id, {
          name: v.name,
          description: v.description,
          startDate: v.startDate,
          releaseDate: v.releaseDate ?? new Date().toISOString().slice(0, 10),
          status: 1,
          sortOrder: v.sortOrder
        }).subscribe({
          next: () => this.reloadProjectData(),
          error: (err) => this.error.set(err?.message || 'Failed to release version.')
        });
      }
    });
  }

  deployVersion(v: ProjectVersionDto): void {
    const notes = this.editingVersionId === v.id ? this.versionReleaseNotes : v.releaseNotes;
    this.pm.deployVersion(v.id, {
      deploymentStatus: v.deploymentStatus ?? 2,
      environment: this.deployEnvironment,
      releaseNotes: notes || undefined
    }).subscribe({
      next: () => this.reloadProjectData(),
      error: (err) => this.error.set(err?.message || 'Deploy failed. The endpoint may not be available yet.')
    });
  }

  updateDeploymentStatus(v: ProjectVersionDto, status: number): void {
    this.pm.deployVersion(v.id, { deploymentStatus: status, environment: this.deployEnvironment }).subscribe({
      next: () => this.reloadProjectData(),
      error: () => {
        this.pm.updateVersion(v.id, {
          name: v.name,
          description: v.description,
          startDate: v.startDate,
          releaseDate: v.releaseDate,
          status: v.status,
          sortOrder: v.sortOrder
        }).subscribe({
          next: () => this.reloadProjectData(),
          error: (err) => this.error.set(err?.message || 'Failed to update deployment status.')
        });
      }
    });
  }

  editVersionNotes(v: ProjectVersionDto): void {
    this.editingVersionId = v.id;
    this.versionReleaseNotes = v.releaseNotes ?? '';
  }

  saveVersionNotes(v: ProjectVersionDto): void {
    this.pm.releaseVersion(v.id, { releaseNotes: this.versionReleaseNotes || undefined }).subscribe({
      next: () => {
        this.editingVersionId = '';
        this.versionReleaseNotes = '';
        this.reloadProjectData();
      },
      error: () => {
        this.pm.updateVersion(v.id, {
          name: v.name,
          description: v.description,
          startDate: v.startDate,
          releaseDate: v.releaseDate,
          status: v.status,
          sortOrder: v.sortOrder
        }).subscribe({
          next: () => {
            this.editingVersionId = '';
            this.reloadProjectData();
          },
          error: (err) => this.error.set(err?.message || 'Failed to save release notes.')
        });
      }
    });
  }

  saveVersionDates(v: ProjectVersionDto, startDate: string, releaseDate: string): void {
    this.pm.updateVersion(v.id, {
      name: v.name,
      description: v.description,
      startDate: startDate || undefined,
      releaseDate: releaseDate || undefined,
      status: v.status,
      sortOrder: v.sortOrder
    }).subscribe({
      next: () => this.reloadProjectData(),
      error: (err) => this.error.set(err?.message || 'Failed to update version.')
    });
  }

  deleteVersion(id: string): void {
    this.pm.deleteVersion(id).subscribe({
      next: () => {
        if (this.selectedVersionId() === id) {
          this.selectedVersionId.set(null);
          this.checklistItems.set([]);
        }
        this.reloadProjectData();
      },
      error: (err) => this.error.set(err?.message || 'Failed to delete version.')
    });
  }

  approveVersion(v: ProjectVersionDto): void {
    this.pm.approveVersion(v.id).subscribe({
      next: () => this.reloadProjectData(),
      error: (err) => this.error.set(err?.message || 'Failed to approve version.')
    });
  }

  rejectVersion(v: ProjectVersionDto): void {
    const reason = prompt('Rejection reason (optional):') ?? undefined;
    this.pm.rejectVersion(v.id, reason || undefined).subscribe({
      next: () => this.reloadProjectData(),
      error: (err) => this.error.set(err?.message || 'Failed to reject version.')
    });
  }

  selectVersionForChecklist(v: ProjectVersionDto): void {
    if (this.selectedVersionId() === v.id) {
      this.selectedVersionId.set(null);
      this.checklistItems.set([]);
      return;
    }
    this.selectedVersionId.set(v.id);
    this.loadChecklist(v.id);
  }

  loadChecklist(versionId: string): void {
    this.loadingChecklist.set(true);
    this.pm.listChecklist(versionId).subscribe({
      next: (items) => {
        this.checklistItems.set(Array.isArray(items) ? items : []);
        this.loadingChecklist.set(false);
      },
      error: () => {
        this.checklistItems.set([]);
        this.loadingChecklist.set(false);
      }
    });
  }

  addChecklistItem(): void {
    const versionId = this.selectedVersionId();
    if (!versionId || !this.newChecklistTitle.trim()) return;
    this.pm.addChecklistItem(versionId, this.newChecklistTitle.trim()).subscribe({
      next: () => {
        this.newChecklistTitle = '';
        this.loadChecklist(versionId);
        this.reloadProjectData();
      },
      error: (err) => this.error.set(err?.message || 'Failed to add checklist item.')
    });
  }

  toggleChecklistItem(item: ReleaseChecklistItemDto): void {
    const versionId = this.selectedVersionId();
    if (!versionId) return;
    this.pm.toggleChecklistItem(versionId, item.id).subscribe({
      next: () => {
        this.loadChecklist(versionId);
        this.reloadProjectData();
      },
      error: (err) => this.error.set(err?.message || 'Failed to toggle checklist item.')
    });
  }

  deleteChecklistItem(item: ReleaseChecklistItemDto): void {
    const versionId = this.selectedVersionId();
    if (!versionId) return;
    this.pm.deleteChecklistItem(versionId, item.id).subscribe({
      next: () => {
        this.loadChecklist(versionId);
        this.reloadProjectData();
      },
      error: (err) => this.error.set(err?.message || 'Failed to delete checklist item.')
    });
  }

  createMilestone(): void {
    const projectId = this.selectedProjectId();
    if (!projectId || !this.milestoneName.trim()) return;
    this.pm.createMilestone({
      projectId,
      name: this.milestoneName.trim(),
      description: this.milestoneDescription || undefined,
      targetDate: this.milestoneTargetDate || undefined,
      versionId: this.milestoneVersionId || undefined,
      status: this.milestoneStatus
    }).subscribe({
      next: () => {
        this.resetMilestoneForm();
        this.reloadProjectData();
      },
      error: (err) => this.error.set(err?.message || 'Failed to create milestone.')
    });
  }

  editMilestone(m: ProjectMilestoneDto): void {
    this.editingMilestoneId = m.id;
    this.milestoneName = m.name;
    this.milestoneDescription = m.description ?? '';
    this.milestoneTargetDate = m.targetDate ?? '';
    this.milestoneVersionId = m.versionId ?? '';
    this.milestoneStatus = m.status;
  }

  saveMilestone(): void {
    if (!this.editingMilestoneId || !this.milestoneName.trim()) return;
    const existing = this.milestones().find(m => m.id === this.editingMilestoneId);
    if (!existing) return;
    this.pm.updateMilestone(this.editingMilestoneId, {
      name: this.milestoneName.trim(),
      description: this.milestoneDescription || undefined,
      targetDate: this.milestoneTargetDate || undefined,
      versionId: this.milestoneVersionId || undefined,
      status: this.milestoneStatus,
      sortOrder: existing.sortOrder
    }).subscribe({
      next: () => {
        this.resetMilestoneForm();
        this.reloadProjectData();
      },
      error: (err) => this.error.set(err?.message || 'Failed to update milestone.')
    });
  }

  deleteMilestone(id: string): void {
    this.pm.deleteMilestone(id).subscribe({
      next: () => this.reloadProjectData(),
      error: (err) => this.error.set(err?.message || 'Failed to delete milestone.')
    });
  }

  resetMilestoneForm(): void {
    this.editingMilestoneId = '';
    this.milestoneName = '';
    this.milestoneDescription = '';
    this.milestoneTargetDate = '';
    this.milestoneVersionId = '';
    this.milestoneStatus = 0;
  }

  appendJqlChip(fragment: string): void {
    if (this.selectedFilterId) return;
    const trimmed = this.jql.trim();
    this.jql = trimmed ? `${trimmed} AND ${fragment}` : fragment;
  }

  deploymentLabel(status?: number, label?: string): string {
    if (label) return label;
    return this.deploymentStatuses.find(d => d.value === status)?.label ?? '—';
  }

  createField(): void {
    const projectId = this.selectedProjectId();
    if (!this.fieldName.trim() || !this.fieldKey.trim()) return;
    this.pm.createCustomField({
      projectId: projectId || undefined,
      name: this.fieldName.trim(),
      key: this.fieldKey.trim().toLowerCase(),
      fieldType: this.fieldType,
      optionsJson: this.fieldOptions || undefined
    }).subscribe({
      next: () => { this.fieldName = ''; this.fieldKey = ''; this.fieldOptions = ''; this.reloadProjectData(); },
      error: (err) => this.error.set(err?.message || 'Failed to create field.')
    });
  }

  deleteField(id: string): void {
    this.pm.deleteCustomField(id).subscribe({
      next: () => this.reloadProjectData(),
      error: (err) => this.error.set(err?.message || 'Failed to delete field.')
    });
  }

  runSearch(): void {
    this.loading.set(true);
    this.pm.search({
      jql: this.selectedFilterId ? undefined : this.jql,
      projectId: this.selectedProjectId() || undefined,
      savedFilterId: this.selectedFilterId || undefined,
      skip: this.searchPage * this.searchPageSize,
      take: this.searchPageSize
    }).subscribe({
      next: (page) => {
        this.searchHits.set(page.items ?? []);
        this.searchTotal.set(page.totalCount ?? 0);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.message || 'Search failed.');
        this.loading.set(false);
      }
    });
  }

  onSearchPage(event: PageEvent): void {
    this.searchPage = event.pageIndex;
    this.searchPageSize = event.pageSize;
    this.runSearch();
  }

  saveFilter(): void {
    if (!this.filterName.trim() || !this.jql.trim()) return;
    this.pm.createFilter({
      name: this.filterName.trim(),
      jql: this.jql.trim(),
      projectId: this.selectedProjectId() || undefined,
      isShared: this.filterShared
    }).subscribe({
      next: () => { this.filterName = ''; this.reloadProjectData(); },
      error: (err) => this.error.set(err?.message || 'Failed to save filter.')
    });
  }

  applyFilter(f: SavedIssueFilterDto): void {
    this.selectedFilterId = f.id;
    this.jql = f.jql;
    this.searchPage = 0;
    this.runSearch();
  }

  clearSelectedFilter(): void {
    this.selectedFilterId = '';
    this.editingFilterId = '';
  }

  startEditFilter(f: SavedIssueFilterDto): void {
    this.editingFilterId = f.id;
    this.filterName = f.name;
    this.jql = f.jql;
    this.filterShared = f.isShared;
  }

  updateFilter(): void {
    if (!this.editingFilterId || !this.filterName.trim() || !this.jql.trim()) return;
    this.pm.updateFilter(this.editingFilterId, {
      name: this.filterName.trim(),
      jql: this.jql.trim(),
      isShared: this.filterShared
    }).subscribe({
      next: () => {
        this.editingFilterId = '';
        this.filterName = '';
        this.reloadProjectData();
      },
      error: (err) => this.error.set(err?.message || 'Failed to update filter.')
    });
  }

  deleteFilter(id: string): void {
    this.pm.deleteFilter(id).subscribe({
      next: () => {
        if (this.selectedFilterId === id) this.selectedFilterId = '';
        if (this.editingFilterId === id) this.editingFilterId = '';
        this.reloadProjectData();
      },
      error: (err) => this.error.set(err?.message || 'Failed to delete filter.')
    });
  }

  loadPortfolios(preselectAccountId?: string | null): void {
    this.loadingPortfolios.set(true);
    this.pm.listPortfolios().subscribe({
      next: (list) => {
        const items = Array.isArray(list) ? list : [];
        this.portfolios.set(items);
        this.portfolioSource.set('api');
        this.loadingPortfolios.set(false);
        this.initPortfolioSelection(preselectAccountId, items);
      },
      error: () => this.loadPortfoliosFromAccounts(preselectAccountId)
    });
  }

  private loadPortfoliosFromAccounts(preselectAccountId?: string | null): void {
    this.api.getPaged<{ id: string; name: string; code?: string; projectCount?: number }>('v1/accounts', { page: 1, pageSize: 100 }).subscribe({
      next: (page) => {
        const fallback: PortfolioSummaryDto[] = (page.items ?? []).map(a => ({
          accountId: a.id,
          accountName: a.name,
          accountCode: a.code,
          projectCount: a.projectCount ?? 0,
          totalIssues: 0,
          overdueIssues: 0,
          blockedIssues: 0,
          completionPercent: 0,
          healthScore: 0,
          health: 0,
          healthLabel: 'Healthy'
        }));
        this.portfolios.set(fallback);
        this.portfolioSource.set('accounts');
        this.loadingPortfolios.set(false);
        this.initPortfolioSelection(preselectAccountId, fallback);
      },
      error: () => {
        this.portfolios.set([]);
        this.loadingPortfolios.set(false);
      }
    });
  }

  private initPortfolioSelection(preselectAccountId: string | null | undefined, items: PortfolioSummaryDto[]): void {
    if (!items.length) return;
    const initial = preselectAccountId && items.some(p => p.accountId === preselectAccountId)
      ? preselectAccountId
      : items[0].accountId;
    this.selectedPortfolioId.set(initial);
    this.loadPortfolioRoadmap();
  }

  onPortfolioChange(id: string): void {
    this.selectedPortfolioId.set(id);
    this.loadPortfolioRoadmap();
  }

  loadPortfolioRoadmap(): void {
    const id = this.selectedPortfolioId();
    if (!id) return;
    this.loadingPortfolioRoadmap.set(true);
    this.pm.getPortfolioRoadmap(id).subscribe({
      next: (data) => {
        this.portfolioRoadmap.set(data);
        this.loadingPortfolioRoadmap.set(false);
      },
      error: () => {
        this.portfolioRoadmap.set(null);
        this.loadingPortfolioRoadmap.set(false);
      }
    });
  }

  healthRagClass = healthRagClass;
}
